<template>
  <component
    :is="tag"
    class="app-row"
    :class="rowClasses"
    :style="rowStyle"
    v-bind="$attrs"
  >
    <slot />
  </component>
</template>

<script>
/**
 * AppRow — fila flexbox con gutters configurables.
 *
 * Reemplazo gradual de b-row / .row.
 * Reglas (spec 005):
 *  - HTML plano, CSS propio, sin tags b-* ni clases Bootstrap.
 *  - Breakpoints sm/md/lg/xl compartidos con AppCol.
 *  - CSS variables para gutters: --app-row-gutter-x, --app-row-gutter-y.
 *
 * Props:
 *  - tag: elemento root. Default 'div'.
 *  - noGutters: elimina padding horizontal de los hijos. Default false.
 *  - gap: gutter custom (number -> px, string -> literal: '1rem', '16px'). Default null.
 *  - align: align-items de flex. Default null.
 *  - justify: justify-content de flex. Default null.
 *  - wrap: flex-wrap. Default true.
 */
const ALIGN_MAP = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline'
}

const JUSTIFY_MAP = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly'
}

export default {
  name: 'AppRow',
  inheritAttrs: false,
  props: {
    tag: { type: String, default: 'div' },
    noGutters: { type: Boolean, default: false },
    gap: { type: [String, Number], default: null },
    align: { type: String, default: null, validator: v => v === null || Object.prototype.hasOwnProperty.call(ALIGN_MAP, v) },
    justify: { type: String, default: null, validator: v => v === null || Object.prototype.hasOwnProperty.call(JUSTIFY_MAP, v) },
    wrap: { type: Boolean, default: true }
  },
  computed: {
    rowClasses() {
      return {
        'app-row--no-gutters': this.noGutters,
        'app-row--no-wrap': !this.wrap,
        [`app-row--align-${this.align}`]: !!this.align,
        [`app-row--justify-${this.justify}`]: !!this.justify
      }
    },
    rowStyle() {
      if (this.gap === null || this.gap === '') return {}
      const g = typeof this.gap === 'number' ? `${this.gap}px` : this.gap
      return {
        '--app-row-gutter-x': g,
        '--app-row-gutter-y': g
      }
    },
    alignCss() {
      return this.align ? ALIGN_MAP[this.align] : null
    },
    justifyCss() {
      return this.justify ? JUSTIFY_MAP[this.justify] : null
    }
  }
}
</script>

<style scoped>
.app-row {
  --app-row-gutter-x: 16px;
  --app-row-gutter-y: 16px;
  display: flex;
  flex-wrap: wrap;
  margin-left: calc(var(--app-row-gutter-x) / -2);
  margin-right: calc(var(--app-row-gutter-x) / -2);
  box-sizing: border-box;
}

.app-row--no-wrap {
  flex-wrap: nowrap;
}

.app-row--no-gutters {
  margin-left: 0;
  margin-right: 0;
}

.app-row--align-start    { align-items: flex-start; }
.app-row--align-center   { align-items: center; }
.app-row--align-end      { align-items: flex-end; }
.app-row--align-stretch  { align-items: stretch; }
.app-row--align-baseline { align-items: baseline; }

.app-row--justify-start   { justify-content: flex-start; }
.app-row--justify-center  { justify-content: center; }
.app-row--justify-end     { justify-content: flex-end; }
.app-row--justify-between { justify-content: space-between; }
.app-row--justify-around  { justify-content: space-around; }
.app-row--justify-evenly  { justify-content: space-evenly; }
</style>